import { defineStore } from 'pinia'
import UserSettingsService from '@/services/UserSettingsService'

// ── Chronotype mapping ──────────────────────────────────────────────────────
// Internal keys used by WeekCanvasStore ↔ API string values
export const CHRONOTYPE_API_TO_INTERNAL = {
  'Morning Lark': 'lark',
  'Night Owl': 'owl',
  'Custom': 'custom',
}

export const CHRONOTYPE_INTERNAL_TO_API = {
  lark: 'Morning Lark',
  owl: 'Night Owl',
  custom: 'Custom',
}

// ── Energy window label → level mapping ────────────────────────────────────
export const ENERGY_LABEL_TO_LEVEL = {
  'Preparation': 'prep',
  'High Energy': 'high',
  'Mid Energy': 'mid',
  'Low Energy': 'low',
}

export const ENERGY_LEVEL_TO_LABEL = {
  prep: 'Preparation',
  high: 'High Energy',
  mid: 'Mid Energy',
  low: 'Low Energy',
}

export const ENERGY_LEVEL_OPTIONS = [
  { value: 'prep', label: 'Preparation' },
  { value: 'high', label: 'High Energy' },
  { value: 'mid', label: 'Mid Energy' },
  { value: 'low', label: 'Low Energy' },
]

// ── Normalizers ─────────────────────────────────────────────────────────────

const extractItem = response =>
  response?.data?.item || response?.data || response?.item || response

/**
 * Convert an API Routine subdoc into the WeekCanvasStore fixedBlock shape.
 * Routines with all 7 active_days become a single block with dayOfWeek: -1.
 * Routines with fewer days are expanded into one block per day.
 */
export function routineToFixedBlocks(routine) {
  const base = {
    _routineId: routine._id,
    label: routine.name || 'Routine',
    startHour: Number(routine.from_hour),
    endHour: Number(routine.to_hour),
    icon: 'mdi-calendar-check-outline',
  }

  const activeDays = routine.active_days ?? [0, 1, 2, 3, 4, 5, 6]

  if (activeDays.length === 7) {
    return [{ ...base, id: `routine-${routine._id}`, dayOfWeek: -1 }]
  }

  // API active_days uses JS Date convention (0=Sun, 1=Mon…6=Sat).
  // WeekCanvas grid uses 0=Mon…6=Sun. Convert before storing dayOfWeek.
  const toWCDay = jsDay => jsDay === 0 ? 6 : jsDay - 1

  return activeDays.map(day => ({
    ...base,
    id: `routine-${routine._id}-day${day}`,
    dayOfWeek: toWCDay(day),
  }))
}

/**
 * Convert an API EnergyWindow subdoc into the WeekCanvasStore energyBlock shape.
 */
export function energyWindowToBlock(win) {
  return {
    _windowId: win._id,
    id: `window-${win._id}`,
    startHour: Number(win.from_hour),
    endHour: Number(win.to_hour),
    level: ENERGY_LABEL_TO_LEVEL[win.label] || 'mid',
    colorCue: win.color_cue || null,
  }
}

// ── Store ───────────────────────────────────────────────────────────────────

export const useUserSettingsStore = defineStore('UserSettingsStore', {
  state: () => ({
    settings: null,
    loading: false,
    saving: false,
    error: null,
  }),

  getters: {
    chronotype: state => state.settings?.chronotype || 'Morning Lark',
    routines: state => state.settings?.routines || [],
    energyWindows: state => state.settings?.energy_windows || [],
    dayStartsAt: state => state.settings?.day_starts_at ?? 5,
    dayEndsAt: state => state.settings?.day_ends_at ?? 23,
  },

  actions: {
    clearError() {
      this.error = null
    },

    _applyResponse(raw) {
      this.settings = extractItem(raw)
    },

    // ── Fetch ──────────────────────────────────────────────────────────────

    async fetchSettings() {
      this.loading = true
      this.error = null

      try {
        const response = await UserSettingsService.getMe()
        this._applyResponse(response)
        return this.settings
      } catch (error) {
        this.error = error.response?.data?.message || error.message || 'Unable to load settings'
        console.error('[UserSettingsStore] fetchSettings:', error)
        return null
      } finally {
        this.loading = false
      }
    },

    // ── Update top-level ───────────────────────────────────────────────────

    async updateSettings(payload) {
      this.saving = true
      this.error = null

      try {
        const response = await UserSettingsService.updateMe(payload)
        this._applyResponse(response)
        return this.settings
      } catch (error) {
        this.error = error.response?.data?.message || error.message || 'Unable to save settings'
        console.error('[UserSettingsStore] updateSettings:', error)
        return null
      } finally {
        this.saving = false
      }
    },

    // ── Routines ───────────────────────────────────────────────────────────

    async addRoutine(payload) {
      this.saving = true
      this.error = null

      try {
        const response = await UserSettingsService.addRoutine(payload)
        this._applyResponse(response)
        return this.settings
      } catch (error) {
        this.error = error.response?.data?.message || error.message || 'Unable to add routine'
        console.error('[UserSettingsStore] addRoutine:', error)
        return null
      } finally {
        this.saving = false
      }
    },

    async updateRoutine(routineId, payload) {
      this.saving = true
      this.error = null

      try {
        const response = await UserSettingsService.updateRoutine(routineId, payload)
        this._applyResponse(response)
        return this.settings
      } catch (error) {
        this.error = error.response?.data?.message || error.message || 'Unable to update routine'
        console.error('[UserSettingsStore] updateRoutine:', error)
        return null
      } finally {
        this.saving = false
      }
    },

    async deleteRoutine(routineId) {
      this.saving = true
      this.error = null

      try {
        const response = await UserSettingsService.deleteRoutine(routineId)
        this._applyResponse(response)
        return this.settings
      } catch (error) {
        this.error = error.response?.data?.message || error.message || 'Unable to delete routine'
        console.error('[UserSettingsStore] deleteRoutine:', error)
        return null
      } finally {
        this.saving = false
      }
    },

    // ── Energy Windows ─────────────────────────────────────────────────────

    async addEnergyWindow(payload) {
      this.saving = true
      this.error = null

      try {
        const response = await UserSettingsService.addEnergyWindow(payload)
        this._applyResponse(response)
        return this.settings
      } catch (error) {
        this.error = error.response?.data?.message || error.message || 'Unable to add energy window'
        console.error('[UserSettingsStore] addEnergyWindow:', error)
        return null
      } finally {
        this.saving = false
      }
    },

    async updateEnergyWindow(windowId, payload) {
      this.saving = true
      this.error = null

      try {
        const response = await UserSettingsService.updateEnergyWindow(windowId, payload)
        this._applyResponse(response)
        return this.settings
      } catch (error) {
        this.error = error.response?.data?.message || error.message || 'Unable to update energy window'
        console.error('[UserSettingsStore] updateEnergyWindow:', error)
        return null
      } finally {
        this.saving = false
      }
    },

    async deleteEnergyWindow(windowId) {
      this.saving = true
      this.error = null

      try {
        const response = await UserSettingsService.deleteEnergyWindow(windowId)
        this._applyResponse(response)
        return this.settings
      } catch (error) {
        this.error = error.response?.data?.message || error.message || 'Unable to delete energy window'
        console.error('[UserSettingsStore] deleteEnergyWindow:', error)
        return null
      } finally {
        this.saving = false
      }
    },
  },
})
